"use client";

import { useEffect, useState } from "react";
import {
  CategoryFilters,
  CategoryFiltersCTA,
  CategoryFiltersProvider,
} from "./category-filters";
import type { CategoryFacets } from "@/lib/api-types";

interface Props {
  facets: CategoryFacets;
  totalAfterFilter: number;
  appliedCount: number;
}

/**
 * Мобильный фильтр-drawer. Кнопка «Фильтры (N)» в тулбаре → полноэкранный
 * sheet из трёх зон: header + scrollable body + sticky footer с CTA.
 *
 * CTA вынесен ВНЕ scrollable области (footer), чтобы избежать Safari 14
 * sticky-в-overflow багов (см. memory/safari14_css_fixup).
 */
export function CategoryFiltersMobile({
  facets,
  totalAfterFilter,
  appliedCount,
}: Props) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="lg:hidden flex h-10 items-center gap-1.5 rounded-[8px] border border-border bg-white px-3 text-sm font-medium text-text shadow-sm active:opacity-80"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M3 6h18M6 12h12M10 18h4" strokeLinecap="round" />
        </svg>
        <span>Фильтры</span>
        {appliedCount > 0 && (
          <span className="ml-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-600 px-1.5 text-xs font-bold text-white">
            {appliedCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Sheet — full screen, flex column */}
          <CategoryFiltersProvider
            facets={facets}
            totalAfterFilter={totalAfterFilter}
            alwaysShowSlider
          >
            <div className="flex h-full w-full flex-col bg-white">
              {/* Header */}
              <div
                className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3"
                style={{ paddingTop: "calc(env(safe-area-inset-top) + 12px)" }}
              >
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-text">Фильтры</h2>
                  {appliedCount > 0 && (
                    <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-100 px-1.5 text-xs font-bold text-brand-700">
                      {appliedCount}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Закрыть"
                  onClick={() => setOpen(false)}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-text-muted hover:bg-bg-muted active:opacity-80"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3">
                <CategoryFilters visibleTop={6} />
              </div>

              {/* Sticky footer — CTA вне scrollable области */}
              <div
                className="shrink-0 border-t border-border bg-white px-4 py-3"
                style={{
                  paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)",
                }}
              >
                <CategoryFiltersCTA />
              </div>
            </div>
          </CategoryFiltersProvider>
        </div>
      )}
    </>
  );
}
